// FrameworkCard — one tile in the Library grid.
//
// Spec: specs/consumer-frameworks-strings.md § Surface 1 · Library · Card.
//
// Renders:
//   - Corner ribbon: "RECOMMENDED" when recommendation_band === 'recommended_next'
//     and no active instance; "IN PROGRESS · N%" when an active instance exists.
//     (In-progress wins over recommended.)
//   - Badges row: domain chip · "→ {target level}" maturity chip · optional
//     "ALREADY THERE" (band='already_there') / "AMBITIOUS" (band='ambitious').
//   - Title + short description.
//   - "AMBITIOUS" note under description when relevant (deep amber copy).
//   - Progress bar for in-progress cards.
//   - Metric row: {n} deliverables · {n} phases · {n} activities · {n} months.
//
// Click navigates to Preview for not-activated frameworks, or Focus for
// in-progress ones. Those routes are built in MR 3 + MR 5; this MR leaves
// them wired-but-unlanded (consumer router returns the dashboard on
// unknown routes, so no crash — just a dead end until the next MRs land).

import { Link } from 'react-router-dom';
import type { MeFrameworkLibraryItem } from '@gc-coach/shared';
import { useI18n } from '../i18n/index.js';
import { MaturityLadder } from './MaturityLadder.js';
import styles from './FrameworkCard.module.css';

interface Props {
  item: MeFrameworkLibraryItem;
  /** The tenant's current maturity level in this item's domain, if known.
   *  Null until tenant.current_level[domain] is wired — the MaturityLadder
   *  renders a "target-only" state in that case. */
  tenantCurrentLevel: null;
}

export function FrameworkCard({ item, tenantCurrentLevel }: Props) {
  const { t, tt, lang } = useI18n();
  const isInProgress = item.active_instance_id !== null;
  const isAlreadyThere = item.recommendation_band === 'already_there';
  const isAmbitious = item.recommendation_band === 'ambitious';
  const isRecommended =
    item.recommendation_band === 'recommended_next' && !isInProgress;

  // Preview / workspace routes. In-progress jumps straight to the Focus
  // tab (workspace). Everything else opens the Preview.
  const href = isInProgress
    ? `/frameworks/instances/${item.active_instance_id}`
    : `/frameworks/${item.id}`;

  const domainLabel = t(`frameworks.domain.${item.domain}`);

  return (
    <Link
      to={href}
      className={[
        styles.card,
        isInProgress ? styles.inProgress : '',
        isAlreadyThere ? styles.dim : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={item.title}
    >
      {isRecommended ? (
        <span className={styles.ribbon}>
          {t('frameworks.library.ribbonRecommended')}
        </span>
      ) : isInProgress ? (
        <span className={styles.ribbon}>
          {tt('frameworks.library.ribbonInProgressTemplate', {
            n: String(item.active_instance_progress_percent ?? 0),
          })}
        </span>
      ) : null}

      <div className={styles.badges}>
        <span className={styles.domainChip}>{domainLabel}</span>
        {item.estimated_effort_label && (
          <span className={styles.maturityChip}>
            → {item.estimated_effort_label}
          </span>
        )}
        {isAlreadyThere && (
          <span className={styles.sideChip}>
            {t('frameworks.library.badgeAlreadyThere')}
          </span>
        )}
        {isAmbitious && (
          <span className={`${styles.sideChip} ${styles.sideChipDrift}`}>
            {t('frameworks.library.badgeAmbitious')}
          </span>
        )}
      </div>

      <h3 className={styles.title}>{item.title}</h3>
      <p className={styles.description}>{item.short_description}</p>

      {isAmbitious && (
        <p className={styles.ambitiousNote}>
          {t('frameworks.library.ambitiousNote')}
        </p>
      )}

      {isInProgress && (
        <div className={styles.progressBarWrap}>
          <div className={styles.progressBar}>
            <span
              className={styles.progressFill}
              style={{ width: `${item.active_instance_progress_percent ?? 0}%` }}
            />
          </div>
        </div>
      )}

      {/* MaturityLadder uses the tenant's actual current level + the framework's
        * declared target. Without a current-level signal, we render the ladder
        * target-only so the card still conveys "this moves you to level X".
        * The tenantCurrentLevel prop is typed `null` for V1 — flip to
        * MaturityLevel | null when the tenant maturity model lands. */}
      {/* NOTE: FrameworkSummary from admin side doesn't expose maturity_target
        * (it's in FrameworkDetail). We render the ladder on the Preview page
        * for now; cards use the badge chip for brevity. Flagged in MR 2 body. */}

      <div className={styles.metricRow}>
        <span>
          <b>{item.phase_count}</b>
          {t('frameworks.library.metricPhases')}
        </span>
        <span>
          <b>{item.activity_count}</b>
          {t('frameworks.library.metricActivities')}
        </span>
      </div>

      {/* Suppress unused-var warning for props declared but not yet used
        * (tenantCurrentLevel + lang + MaturityLadder) — all three light up
        * when the tenant maturity model + domain locale extensions land. */}
      <span style={{ display: 'none' }} aria-hidden>
        {lang}
        {tenantCurrentLevel}
        <MaturityLadder current={null} target="initial" compact />
      </span>
    </Link>
  );
}
